import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import Moment from 'moment-timezone';
import * as XLSX from 'xlsx';
import { Button, ButtonToolbar } from 'reactstrap';
import Loader from '../../../common/Loader';
import Table from '../../../common/Table';
import Badge from '../../../common/Badge';
import {
  REPORT_DATE_TIME_SECONDS_FORMAT,
  TIME_ZONE,
} from '../../../../config/locale';
import {
  BASE_FUEL_REFILL_PATH,
  BUS_PATH,
  CIRCUIT_PATH,
  generatePurchaseOrderViewPath,
  ITINERARY_FOR_COUNTER_PATH,
  ITINERARY_GROUP_PATH,
} from '../../../../config/paths';
import { DEFAULT_PAGE_SIZE } from '../../../../config/constants';
import { filterMethodCaseInsensitive } from '../../../../utils/filter-methods';
import { numberFormatter } from '../../../../utils/number';
import './FuelDataCompletenessReportSearchResult.css';

const MISSING_LABELS = [
  ['missingCircuitGroup', 'Sin grupo circuito'],
  ['missingBaseFuelRefillOnFinalItinerary', 'Sin relleno base (it. final)'],
  [
    'multipleBaseFuelRefillsOnFinalItinerary',
    'Varios rellenos en base en itinerario final',
  ],
  ['missingApprovedPurchaseOrder', 'OC no aprobada'],
  ['missingAuthorizedAllocation', 'Sin dotación'],
  ['missingKilometers', 'Sin km'],
  ['missingKmPerGallon', 'Sin km/galón'],
];

const INFO_ALERT_KEYS = {
  multipleBaseFuelRefillsOnFinalItinerary: true,
};

/** Circuit-group saving threshold (gal): real below allocation by more than this amount. */
const CIRCUIT_GROUP_SAVING_GALLONS_THRESHOLD = 15;

const toGalNumber = (value) => {
  if (value == null || value === '') {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Returns 'excess' when circuit real consumption exceeds circuit allocation,
 * or 'saving' when real is below allocation by more than CIRCUIT_GROUP_SAVING_GALLONS_THRESHOLD gal.
 */
const getCircuitGroupConsumptionHighlight = (row) => {
  const dotacion = toGalNumber(row.circuitAuthorizedFuelAllocation);
  const real = toGalNumber(row.circuitRealFuelConsumption);
  if (dotacion == null || real == null) {
    return null;
  }
  if (real > dotacion) {
    return 'excess';
  }
  if (dotacion - real > CIRCUIT_GROUP_SAVING_GALLONS_THRESHOLD) {
    return 'saving';
  }
  return null;
};

const circuitGroupConsumptionAlertLabel = (row) => {
  const kind = getCircuitGroupConsumptionHighlight(row);
  if (kind === 'excess') {
    return 'Exceso de consumo';
  }
  if (kind === 'saving') {
    return 'Ahorro de consumo (>15 gal)';
  }
  return null;
};

const rowDataFromTableRow = (row) => {
  if (!row) {
    return null;
  }
  return row.original || row._original || row;
};

const generateTrClassName = (row) =>
  getCircuitGroupConsumptionHighlight(rowDataFromTableRow(row))
    ? 'fuel-circuit-consumption-danger'
    : '';

const parseDepartureMoment = (departureTime) => {
  if (departureTime == null || departureTime === '') {
    return null;
  }
  if (typeof departureTime === 'number' && Number.isFinite(departureTime)) {
    return Moment(departureTime);
  }
  const s = String(departureTime).trim();
  if (/^\d+$/.test(s)) {
    return Moment(Number(s));
  }
  return Moment(s);
};

const formatDeparture = (departureTime) => {
  const m = parseDepartureMoment(departureTime);
  if (!m || !m.isValid()) {
    return '—';
  }
  return m.tz(TIME_ZONE).format(REPORT_DATE_TIME_SECONDS_FORMAT);
};

const fmtDec = (value, minFrac = 2, maxFrac = 2) =>
  value != null && value !== ''
    ? numberFormatter({
        style: 'decimal',
        value,
        minimumFractionDigits: minFrac,
        maximumFractionDigits: maxFrac,
      })
    : '—';

const cellInt = (value) => (value != null && value !== '' ? String(value) : '—');

const kmItinerary = (row) =>
  row.itineraryKilometers != null ? row.itineraryKilometers : row.kilometers;

const alertsTextForRow = (row) =>
  MISSING_LABELS.filter(([key]) => row[key])
    .map(([, label]) => label)
    .join(' | ');

/** When alert chips are selected, keep rows that match at least one chip (OR). */
const filterRowsByAlertChips = (rows, alertChips) => {
  if (!alertChips || alertChips.length === 0) {
    return rows;
  }
  return rows.filter((row) =>
    alertChips.some((chip) => !!row[chip.key]),
  );
};

const filterDepartureFormatted = (filter, row) => {
  const raw = filter.value;
  if (raw == null || !String(raw).trim()) {
    return true;
  }
  const source = row._original || row;
  const needle = String(raw).trim().toLowerCase();
  const hay = formatDeparture(source.departureTime);
  if (!hay || hay === '—') {
    return false;
  }
  return hay.toLowerCase().includes(needle);
};

const buildColumns = (onToggleAlertChip) => [
  {
    Header: 'Salida',
    id: 'departureTime',
    accessor: 'departureTime',
    filterMethod: filterDepartureFormatted,
    Cell: ({ original }) =>
      original.departureTime ? formatDeparture(original.departureTime) : '—',
    className: 'text-center',
    minWidth: 150,
  },
  {
    Header: 'Bus',
    accessor: (row) =>
      row.companyBusId != null ? row.companyBusId : '',
    id: 'companyBusId',
    Cell: ({ value, original }) =>
      original.companyBusId != null &&
      String(original.companyBusId).trim() !== '' ? (
        <Link to={`${BUS_PATH}/${original.companyBusId}`}>
          {original.companyBusId}
        </Link>
      ) : (
        '—'
      ),
    className: 'text-center',
    minWidth: 96,
  },
  {
    Header: 'Ciudad origen',
    accessor: (row) =>
      row.originCityName != null ? row.originCityName : '',
    id: 'originCityName',
    Cell: ({ value }) => (value ? value : '—'),
    className: 'text-center',
    minWidth: 110,
  },
  {
    Header: 'Ciudad destino',
    accessor: (row) =>
      row.destinationCityName != null ? row.destinationCityName : '',
    id: 'destinationCityName',
    Cell: ({ value }) => (value ? value : '—'),
    className: 'text-center',
    minWidth: 110,
  },
  {
    Header: 'Itinerario',
    accessor: (row) => (row.itineraryId != null ? row.itineraryId : ''),
    id: 'itineraryId',
    Cell: ({ value }) =>
      value !== '' && value != null ? (
        <Link to={`${ITINERARY_FOR_COUNTER_PATH}/${value}`}>{value}</Link>
      ) : (
        '—'
      ),
    className: 'text-center',
    minWidth: 112,
  },
  {
    Header: 'Circuito',
    accessor: (row) => (row.circuitId != null ? row.circuitId : ''),
    id: 'circuitId',
    Cell: ({ value }) =>
      value !== '' && value != null ? (
        <Link to={`${CIRCUIT_PATH}/${value}`}>{value}</Link>
      ) : (
        '—'
      ),
    className: 'text-center',
    minWidth: 80,
  },
  {
    Header: 'Grupo circuito',
    accessor: (row) => (row.circuitGroupId != null ? row.circuitGroupId : ''),
    id: 'circuitGroupId',
    Cell: ({ value }) =>
      value !== '' && value != null ? (
        <Link to={`${ITINERARY_GROUP_PATH}/${value}`}>{value}</Link>
      ) : (
        '—'
      ),
    className: 'text-center',
    minWidth: 130,
  },
  {
    Header: 'Tipo circuito',
    accessor: (row) => (row.circuitType != null ? row.circuitType : ''),
    id: 'circuitType',
    Cell: ({ value }) => (value ? value : '—'),
    className: 'text-center',
    minWidth: 100,
  },
  {
    Header: 'Vales CG',
    accessor: 'circuitGroupVoucherCount',
    Cell: ({ value }) => cellInt(value),
    className: 'text-center',
    minWidth: 88,
  },
  {
    Header: 'Vales facturados',
    accessor: 'circuitGroupInvoicedVoucherCount',
    Cell: ({ value }) => cellInt(value),
    className: 'text-center',
    minWidth: 110,
  },
  {
    Header: 'Ord. de compra',
    accessor: (row) => (row.purchaseOrderId != null ? row.purchaseOrderId : ''),
    id: 'purchaseOrderId',
    Cell: ({ value }) =>
      value !== '' && value != null ? (
        <Link to={generatePurchaseOrderViewPath(value)}>{value}</Link>
      ) : (
        '—'
      ),
    className: 'text-center',
    minWidth: 110,
  },
  {
    Header: 'Relleno base',
    accessor: (row) =>
      row.baseFuelRefillId != null ? row.baseFuelRefillId : '',
    id: 'baseFuelRefillId',
    Cell: ({ value }) =>
      value !== '' && value != null ? (
        <Link to={`${BASE_FUEL_REFILL_PATH}/${value}`}>{value}</Link>
      ) : (
        '—'
      ),
    className: 'text-center',
    minWidth: 100,
  },
  {
    Header: 'Gal. relleno base',
    accessor: 'baseFuelRefillGallons',
    id: 'baseFuelRefillGallons',
    Cell: ({ value }) => fmtDec(value),
    className: 'text-center',
    minWidth: 110,
  },
  {
    Header: 'Gal. ralentí',
    accessor: 'idlingAdditionalGallons',
    Cell: ({ value }) => fmtDec(value),
    className: 'text-center',
    minWidth: 96,
  },
  {
    Header: 'Dotación (gal)',
    accessor: 'authorizedFuelAllocation',
    Cell: ({ value }) => fmtDec(value),
    className: 'text-center',
    minWidth: 110,
  },
  {
    Header: 'Cons. real ruta (gal)',
    accessor: 'routeRealFuelConsumption',
    Cell: ({ value }) => fmtDec(value),
    className: 'text-center',
    minWidth: 130,
  },
  {
    Header: 'Dotación circuito',
    accessor: 'circuitAuthorizedFuelAllocation',
    Cell: ({ value }) => fmtDec(value),
    className: 'text-center',
    minWidth: 120,
  },
  {
    Header: 'Cons. real circuito',
    accessor: 'circuitRealFuelConsumption',
    Cell: ({ value }) => fmtDec(value),
    className: 'text-center',
    minWidth: 130,
  },
  {
    Header: 'Extraord.',
    id: 'extraordinaryItinerary',
    accessor: (row) =>
      row.extraordinaryItinerary === true || row.extraordinaryItinerary === 'true'
        ? 'Sí'
        : 'No',
    Cell: ({ value }) => value,
    className: 'text-center',
    minWidth: 84,
  },
  {
    Header: 'Km (It)',
    id: 'kmIt',
    accessor: (row) => kmItinerary(row),
    Cell: ({ value }) => fmtDec(value),
    className: 'text-center',
    minWidth: 80,
  },
  {
    Header: 'Km (CG)',
    accessor: 'circuitGroupKilometers',
    Cell: ({ value }) => fmtDec(value),
    className: 'text-center',
    minWidth: 80,
  },
  {
    Header: 'Km/gal',
    accessor: 'kilometersPerGallon',
    Cell: ({ value }) => fmtDec(value, 2, 4),
    className: 'text-center',
    minWidth: 80,
  },
  {
    Header: 'Alertas',
    id: 'alertsText',
    accessor: (row) => alertsTextForRow(row),
    filterable: false,
    Cell: ({ original }) => (
      <div className="d-flex flex-wrap justify-content-center">
        {MISSING_LABELS.map(([key, label]) =>
          original[key] ? (
            <span
              key={key}
              className="m-1"
              style={{ cursor: 'pointer' }}
              role="button"
              tabIndex={0}
              title="Filtrar por esta alerta (clic de nuevo para quitar; se pueden combinar varias)"
              onClick={(e) => {
                e.stopPropagation();
                onToggleAlertChip(key, label);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onToggleAlertChip(key, label);
                }
              }}
            >
              <Badge
                color={INFO_ALERT_KEYS[key] ? 'info' : 'warning'}
                text={label}
              />
            </span>
          ) : null,
        )}
      </div>
    ),
    sortable: false,
    minWidth: 220,
  },
];

const formatPercent = (value) =>
  typeof value === 'number' && Number.isFinite(value)
    ? `${value.toFixed(1)} %`
    : '—';

const percentOfTotal = (part, total) => {
  if (
    typeof part !== 'number' ||
    typeof total !== 'number' ||
    !Number.isFinite(part) ||
    !Number.isFinite(total) ||
    total <= 0
  ) {
    return null;
  }
  return (part / total) * 100;
};

const exportRowPlain = (row) => ({
  Salida: formatDeparture(row.departureTime),
  Bus:
    row.companyBusId != null && String(row.companyBusId).trim() !== ''
      ? String(row.companyBusId)
      : '—',
  'Ciudad origen': row.originCityName || '—',
  'Ciudad destino': row.destinationCityName || '—',
  Itinerario: row.itineraryId != null ? String(row.itineraryId) : '—',
  Circuito: row.circuitId != null ? String(row.circuitId) : '—',
  'Grupo circuito':
    row.circuitGroupId != null ? String(row.circuitGroupId) : '—',
  'Tipo circuito': row.circuitType || '—',
  'Vales CG': cellInt(row.circuitGroupVoucherCount),
  'Vales facturados': cellInt(row.circuitGroupInvoicedVoucherCount),
  'Ord. de compra':
    row.purchaseOrderId != null ? String(row.purchaseOrderId) : '—',
  'Relleno base':
    row.baseFuelRefillId != null ? String(row.baseFuelRefillId) : '—',
  'Gal. relleno base': fmtDec(row.baseFuelRefillGallons),
  'Gal. ralentí': fmtDec(row.idlingAdditionalGallons),
  'Dotación (gal)': fmtDec(row.authorizedFuelAllocation),
  'Cons. real ruta (gal)': fmtDec(row.routeRealFuelConsumption),
  'Dotación circuito': fmtDec(row.circuitAuthorizedFuelAllocation),
  'Cons. real circuito': fmtDec(row.circuitRealFuelConsumption),
  'Alerta consumo (CG)': circuitGroupConsumptionAlertLabel(row) || '—',
  'Extraord.':
    row.extraordinaryItinerary === true || row.extraordinaryItinerary === 'true'
      ? 'Sí'
      : 'No',
  'Km (It)': fmtDec(kmItinerary(row)),
  'Km (CG)': fmtDec(row.circuitGroupKilometers),
  'Km/gal': fmtDec(row.kilometersPerGallon, 2, 4),
  Alertas: alertsTextForRow(row) || '—',
});

const buildSummarySheetAoa = (summary) => {
  if (!summary) {
    return [['Sin resumen']];
  }
  const total = summary.total;
  const complete = summary.complete;
  const withAlerts =
    total != null && complete != null ? Math.max(0, total - complete) : null;
  const pctC = percentOfTotal(complete, total);
  const pctA = percentOfTotal(withAlerts, total);
  const byAlert = Array.isArray(summary.byAlert) ? summary.byAlert : [];
  const rows = [
    ['Reporte de Seguimiento de Combustible — resumen'],
    [],
    ['Total itinerarios', total != null ? total : '—'],
    ['Sin alertas (completos)', complete != null ? complete : '—'],
    [
      '% completos / total',
      pctC != null ? `${pctC.toFixed(1)} %` : '—',
    ],
    ['Con al menos una alerta', withAlerts != null ? withAlerts : '—'],
    [
      '% con alertas / total',
      pctA != null ? `${pctA.toFixed(1)} %` : '—',
    ],
    [],
    ['Alerta', 'Cantidad', '% sobre total'],
    ...byAlert.map((item) => [
      item.label || item.key || '—',
      item.count != null ? item.count : '—',
      formatPercent(item.percent),
    ]),
  ];
  return rows;
};

const exportFuelDataCompletenessReportToExcel = (rows, summary) => {
  const detail = rows.map(exportRowPlain);
  const wb = XLSX.utils.book_new();
  const wsDetail = XLSX.utils.json_to_sheet(detail);
  XLSX.utils.book_append_sheet(wb, wsDetail, 'Detalle');
  const wsRes = XLSX.utils.aoa_to_sheet(buildSummarySheetAoa(summary));
  XLSX.utils.book_append_sheet(wb, wsRes, 'Resumen');
  const stamp = Moment().tz(TIME_ZONE).format('YYYYMMDD-HHmmss');
  XLSX.writeFile(wb, `seguimiento-combustible-${stamp}.xlsx`);
};

export class FuelDataCompletenessReportSearchResult extends Component {
  constructor(props) {
    super(props);
    this.state = { alertChips: [] };
    this.columns = buildColumns(this.toggleAlertChip);
  }

  toggleAlertChip = (alertKey, label) => {
    this.setState((prev) => {
      const exists = prev.alertChips.some((c) => c.key === alertKey);
      if (exists) {
        return {
          alertChips: prev.alertChips.filter((c) => c.key !== alertKey),
        };
      }
      return {
        alertChips: [
          ...prev.alertChips,
          {
            id: `${Date.now()}_${String(Math.random()).slice(2, 11)}`,
            key: alertKey,
            label,
          },
        ],
      };
    });
  };

  clearAlertChips = () => {
    this.setState({ alertChips: [] });
  };

  handleExportExcel = () => {
    const { report } = this.props;
    const rows = report && report.get('rows') ? report.get('rows').toJS() : [];
    const summary =
      report && report.get('summary') ? report.get('summary').toJS() : null;
    if (!rows.length) {
      return;
    }
    exportFuelDataCompletenessReportToExcel(rows, summary);
  };

  render() {
    const { loading, report } = this.props;
    const { alertChips } = this.state;

    if (loading) {
      return <Loader />;
    }

    const allRows =
      report && report.get('rows') ? report.get('rows').toJS() : [];
    const rowsForTable = filterRowsByAlertChips(allRows, alertChips);
    const summary =
      report && report.get('summary') ? report.get('summary').toJS() : null;
    const totalFromApi =
      report && report.get('total') != null ? report.get('total') : allRows.length;
    const byAlert =
      summary && Array.isArray(summary.byAlert) ? summary.byAlert : [];

    const summaryTotal =
      summary && summary.total != null ? Number(summary.total) : null;
    const summaryComplete =
      summary && summary.complete != null ? Number(summary.complete) : null;
    const summaryWithAlerts =
      summaryTotal != null && summaryComplete != null
        ? Math.max(0, summaryTotal - summaryComplete)
        : null;
    const pctComplete =
      summaryComplete != null
        ? percentOfTotal(summaryComplete, summaryTotal)
        : null;
    const pctWithAlerts =
      summaryWithAlerts != null
        ? percentOfTotal(summaryWithAlerts, summaryTotal)
        : null;

    let resultsTitleSuffix;
    if (alertChips.length > 0) {
      resultsTitleSuffix =
        rowsForTable.length !== allRows.length
          ? ` (mostrando ${rowsForTable.length} de ${allRows.length})`
          : ` (${rowsForTable.length})`;
    } else if (totalFromApi !== allRows.length) {
      resultsTitleSuffix = ` (${allRows.length} de ${totalFromApi})`;
    } else {
      resultsTitleSuffix = ` (${allRows.length})`;
    }

    return (
      <div className="mt-4">
        {summary && (
          <div className="mb-4 p-3 border rounded bg-light">
            <h5 className="mb-3">Resumen</h5>
            <div className="row mb-3">
              <div className="col-md-4">
                <strong>Total itinerarios</strong>
                <div>{summary.total != null ? summary.total : '—'}</div>
              </div>
              <div className="col-md-4">
                <strong>Sin alertas (completos)</strong>
                <div>
                  {summary.complete != null ? summary.complete : '—'}
                  {pctComplete != null && (
                    <div className="text-muted small mt-1">
                      {formatPercent(pctComplete)} del total
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-4">
                <strong>Con al menos una alerta</strong>
                <div>
                  {summaryWithAlerts != null ? summaryWithAlerts : '—'}
                  {pctWithAlerts != null && (
                    <div className="text-muted small mt-1">
                      {formatPercent(pctWithAlerts)} del total
                    </div>
                  )}
                </div>
              </div>
            </div>
            <h6 className="mb-2">Alertas (conteo y % sobre el total)</h6>
            <div className="table-responsive">
              <table className="table table-sm table-bordered bg-white mb-0">
                <thead>
                  <tr>
                    <th>Alerta</th>
                    <th className="text-end">Cantidad</th>
                    <th className="text-end">%</th>
                  </tr>
                </thead>
                <tbody>
                  {byAlert.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="text-muted">
                        Sin datos de resumen
                      </td>
                    </tr>
                  ) : (
                    byAlert.map((item) => (
                      <tr key={item.key || item.label}>
                        <td>{item.label || item.key}</td>
                        <td className="text-end">
                          {item.count != null ? item.count : '—'}
                        </td>
                        <td className="text-end">
                          {formatPercent(item.percent)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center flex-wrap fuel-data-completeness-report-result-toolbar">
          <h4 className="mb-0">
            Resultados
            {resultsTitleSuffix}
          </h4>
          <ButtonToolbar className="fuel-data-completeness-report-chip-toolbar d-flex flex-wrap align-items-center justify-content-end mb-0">
            <Button
              color="success"
              size="sm"
              className="mb-1"
              disabled={allRows.length === 0}
              onClick={this.handleExportExcel}
            >
              <i className="fa fa-file-excel-o mr-1" />
              Exportar a Excel
            </Button>
          </ButtonToolbar>
        </div>

        {allRows.length > 0 && (
          <div className="mb-3 p-2 border rounded bg-white fuel-data-completeness-report-alert-type-chips">
            <div className="small text-secondary mb-2">
              Filtrar por tipo de alerta (OR: basta con que el itinerario tenga una de las
              seleccionadas; clic otra vez en el mismo chip para quitarlo)
            </div>
            <div className="d-flex flex-wrap align-items-center">
              {MISSING_LABELS.map(([key, label]) => {
                const active = alertChips.some((c) => c.key === key);
                const info = INFO_ALERT_KEYS[key];
                return (
                  <button
                    key={key}
                    type="button"
                    className="btn btn-sm mb-1 mr-2 py-1 px-2 border-0"
                    style={{
                      background: 'transparent',
                      boxShadow: active ? 'inset 0 0 0 2px #007bff' : 'none',
                      borderRadius: '4px',
                    }}
                    title={
                      active
                        ? 'Quitar este filtro'
                        : 'Mostrar solo itinerarios con esta alerta'
                    }
                    onClick={() => this.toggleAlertChip(key, label)}
                  >
                    <Badge
                      color={info ? 'info' : 'warning'}
                      text={label}
                      pill
                    />
                  </button>
                );
              })}
              {alertChips.length > 0 && (
                <Button
                  color="link"
                  size="sm"
                  className="py-0 mb-1"
                  onClick={this.clearAlertChips}
                >
                  Quitar todos los filtros de alerta
                </Button>
              )}
            </div>
          </div>
        )}

        {allRows.length > 0 && (
          <p className="small text-muted mb-2">
            Fila en rojo (grupo circuito): exceso si el consumo real circuito supera la
            dotación circuito; ahorro si el real es menor en más de{' '}
            {CIRCUIT_GROUP_SAVING_GALLONS_THRESHOLD} gal respecto a esa dotación.
          </p>
        )}

        <div className="fuel-data-completeness-report-table-scroll">
          <Table
            manual={false}
            columns={this.columns}
            data={rowsForTable}
            defaultPageSize={DEFAULT_PAGE_SIZE}
            loading={false}
            defaultFilterMethod={filterMethodCaseInsensitive}
            filterable
            showPagination
            generateTrClassName={generateTrClassName}
          />
        </div>
      </div>
    );
  }
}

FuelDataCompletenessReportSearchResult.propTypes = {
  loading: PropTypes.bool,
  report: PropTypes.instanceOf(Immutable.Map),
};

FuelDataCompletenessReportSearchResult.defaultProps = {
  loading: false,
  report: null,
};

const mapStateToProps = ({ TrafficUnit }) => ({
  loading: !TrafficUnit.FuelDataCompletenessReport.getIn([
    'current',
    'activity',
  ]).isEmpty(),
  report: TrafficUnit.FuelDataCompletenessReport.getIn([
    'current',
    'content',
  ]),
});

export default connect(mapStateToProps)(FuelDataCompletenessReportSearchResult);
